import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Banner } from '@els/els-react--banner';
import { Button } from '@els/els-react--button';
import { Card } from '@els/els-react--card';
import { Modal } from '@els/els-react--modal';
import { Dropdown } from '@els/els-react--dropdown';
import { TextInput } from '@els/els-react--text-input';
import { Icon } from '@els/els-react--icon';
import take from 'lodash/take';
import toArray from 'lodash/toArray';
import isArray from 'lodash/isArray';
import { StructureFromNameOptions, StructureFromNameTexts } from '@Pages/StructureEditor/StructureEditorConstants';
import { STRUCTURE_INSERT_MODE_LOAD } from '@Pages/StructureEditor/constants';
import {
  MODAL_SPINNER_ID,
  MIN_QUERY_LENGTH,
  COUNT_SHOW_SUBSTANCES,
} from '@Pages/StructureEditor/Components/StructureFromNameModalConstants';
import * as Spinners from '@Services/Spinners';
import StructureFromName from '@Services/StructureFromName';
import Catalyst from '@Services/Catalyst';
import Spinner from '@Components/Spinner';
import { trackButton } from '@Pages/StructureEditor/analytics';

function StructureFromNameModal({ closeModal, addStructureToEditor }) {
  const modalQueryOptions = toArray(StructureFromNameOptions);
  const [showModalBanner, setShowModalBanner] = useState(false);
  const [modalBannerText, setModalBannerText] = useState(StructureFromNameTexts?.nameNotFoundMessage);
  const [modalQuery, setModalQuery] = useState('');
  const [selectQueryOption, setSelectQueryOption] = useState({
    label: StructureFromNameOptions?.IS?.viewValue,
    id: StructureFromNameOptions?.IS?.serverValue,
  });
  const [allSubstancesModal, setAllSubstancesModal] = useState(null);
  const [modalSubstances, setModalSubstances] = useState(null);
  const fromNameAllSubstancesRef = useRef(null);
  const queryInputRef = useRef(null);

  function structureFromNameSearch(structureFromName, structureNameFromName) {
    addStructureToEditor(structureFromName, structureNameFromName, STRUCTURE_INSERT_MODE_LOAD);
    trackButton('submit structure from name');
    closeModal();
  }

  function modalStructureSearch() {
    const value = modalQuery;
    setShowModalBanner(false);
    Logger.info(`Structure search: ${JSON.stringify(selectQueryOption)} ${value}`);
    if (value && value.length >= MIN_QUERY_LENGTH && selectQueryOption) {
      Spinners.activate(MODAL_SPINNER_ID);

      StructureFromName.getMolForStructure(value, selectQueryOption?.id)
        .then((response) => {
          if (isArray(response.substances)) {
            const allSubstances = response.substances;
            setAllSubstancesModal(allSubstances);

            if (allSubstances.length === 1) {
              structureFromNameSearch(allSubstances[0]?.structure, value);
            } else {
              setModalSubstances(allSubstances.slice(0, COUNT_SHOW_SUBSTANCES));
            }
          } else {
            setShowModalBanner(true);
            setModalBannerText(StructureFromNameTexts?.nameNotFoundMessage);
            Catalyst.trackError({
              component: 'Structure from name',
            });
          }
        })
        .catch(Logger.error)
        .finally(() => {
          Logger.info('Terminate spinner');
          Spinners.terminate(MODAL_SPINNER_ID);
        });
    }
  }

  const handleSubmit = useCallback(() => {
    modalStructureSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalQuery, selectQueryOption, modalSubstances, showModalBanner, modalBannerText]);

  useEffect(() => {
    const handleScroll = () => {
      const container = fromNameAllSubstancesRef.current;
      if (container.scrollTop + container.clientHeight >= container.scrollHeight) {
        if (modalSubstances < allSubstancesModal) {
          setModalSubstances(take(allSubstancesModal, modalSubstances.length + COUNT_SHOW_SUBSTANCES));
        }
      }
    };

    const container = fromNameAllSubstancesRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [modalSubstances, allSubstancesModal]);

  useLayoutEffect(() => {
    queryInputRef.current.focus();
  }, []);

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Enter') {
        handleSubmit();
      }
    };

    const input = queryInputRef.current;

    if (input) {
      input.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      if (input) {
        input.removeEventListener('keydown', handleKeyPress);
      }
    };
  }, [modalQuery, selectQueryOption, handleSubmit]);

  return (
    <Modal id="structure-from-name-modal" className="insert-from-name-modal" onClose={() => closeModal()}>
      <div className="u-els-height-1o4">
        <h4 id="dialog-title">Create structure template from name</h4>
        <Banner
          id="structureFromName-banner"
          isVisible={showModalBanner}
          isIconVisible={false}
          hideCloseLabel
          onClose={() => {
            setShowModalBanner(false);
          }}>
          {modalBannerText}
        </Banner>
        <div className="u-els-view-width-1o2 o-els-flex-layout o-els-flex-layout--gutters u-els-padding-1x">
          <Dropdown
            id="structureFromName-options"
            className="o-els-flex-layout__item u-els-padding-1o2"
            onChange={(e) => setSelectQueryOption(e)}
            options={modalQueryOptions.map((option) => ({ label: option.viewValue, id: option.serverValue }))}
            selected={selectQueryOption}
            showSelectedAsLabel
          />
          <TextInput
            id="structureFromName-query"
            inputRef={queryInputRef}
            className="o-els-flex-layout__item o-els-flex-layout__item--grow"
            value={modalQuery}
            onChange={(e) => setModalQuery(e.target.value)}
            shouldHideLabel
            required
            label="Enter a chemical name, CAS-RN, InChiKey or SMILES"
            iconRight={{
              sprite: 'Search',
              a11y: {
                name: 'Search',
              },
            }}
            iconRightCallback={() => modalStructureSearch()}
          />
        </div>

        <Spinner spinnerId={MODAL_SPINNER_ID} />

        <div className="substances-container">
          <div ref={fromNameAllSubstancesRef} className="substances">
            {modalSubstances &&
              modalSubstances.map((substance) => (
                <Card key={substance?.rxId} className="u-els-margin-bottom-1x u-els-margin-right-1x">
                  <div className="o-els-flex-layout">
                    <div className="o-els-flex-layout__item substance-card-item">
                      <img src={substance?.path} alt={substance?.substanceNames && substance?.substanceNames[0]?.value} draggable="false" />
                    </div>
                    <div className="o-els-flex-layout__item--grow substance-card-item">
                      <div>{substance?.substanceNames && substance?.substanceNames[0].value}</div>
                      {substance?.referencesNumber && <div>{substance?.referencesNumber} references</div>}
                    </div>
                    <div className="o-els-flex-layout__item substance-card-item substance-button-container">
                      <Button
                        id={`button-${substance?.rxId}`}
                        type="secondary"
                        onClick={() =>
                          structureFromNameSearch(substance?.structure, substance?.substanceNames && substance?.substanceNames[0]?.value)
                        }>
                        <Icon a11y={{ name: 'Load template' }} id={`icon-${substance?.rxId}`} isVisible isTextFirst sprite="ChevronRight">
                          Load template
                        </Icon>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}

StructureFromNameModal.propTypes = {
  closeModal: PropTypes.func,
  addStructureToEditor: PropTypes.func,
};

export default StructureFromNameModal;
